import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Card, Form, Spinner } from 'react-bootstrap';

import { GetPushBroadcastPreviewQuery, GetPushBroadcastPreviewQueryVariables } from '../../../../../../__gqltypes__';
import { GET_PUSH_BROADCAST_PREVIEW } from '../graphql';
import {
  PushBroadcastLanguageOption,
  pushBroadcastErrorIssues,
  pushLanguageName,
  pushSampleSegments,
  PushTexts,
} from '../utils';
import { SolidBadge } from '../../../../../../utils/components/SolidBadge';
import { PushIssueList } from './PushIssueList';

type PreviewData = GetPushBroadcastPreviewQuery['pushBroadcastPreview'];
type Surface = PreviewData['surfaces'][0];

type Props = {
  // Null: not saved yet (the preview renders a saved broadcast, with the editor's unsaved texts).
  broadcastId: string | null;
  titleTexts: PushTexts;
  bodyTexts: PushTexts;
  languages: ReadonlyArray<PushBroadcastLanguageOption>;
  sourceLanguage: string;
  overrideLanguages: ReadonlyArray<string>;
};

const EDIT_DEBOUNCE_MS = 400;

// The editor's texts, once typing paused: one preview request per pause, not per key.
function useSettled<T>(value: T): T {
  const [settled, setSettled] = useState(value);
  const key = JSON.stringify(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(JSON.parse(key)), EDIT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]);
  return settled;
}

function Marked({ text, samples }: { text: string; samples: ReadonlyArray<string> }): JSX.Element {
  return (
    <>
      {pushSampleSegments(text, samples).map((segment, index) =>
        segment.isSample ? (
          <mark key={index} className="px-0" title="Sample value: each player sees their own">
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      )}
    </>
  );
}

function cutLine(part: 'Title' | 'Body', text: Surface['title']): JSX.Element {
  return text.isCut ? (
    <div className="text-danger">
      {part} cut after {text.limit} of its {text.length} characters
    </div>
  ) : (
    <div className="text-muted">
      {part} fits ({text.length} / {text.limit})
    </div>
  );
}

/**
 * One lock screen, framed like the Custom Popup preview frames a popup: a backdrop standing in for the phone's screen,
 * and on it the notification drawn with the platform's own shape (iOS: icon beside the texts; Android: app row above
 * them), never a bordered box in the form. It shows exactly what the server says that surface shows (cut with an
 * ellipsis), never the full string in a wide box, and follows the admin theme, as a phone in light or dark mode would
 * (theme.css `.push-device-stage`, `.push-notification`).
 */
function SurfaceMock({ surface, samples }: { surface: Surface; samples: ReadonlyArray<string> }): JSX.Element {
  const title = (
    <div className="push-notification__title">
      <Marked text={surface.title.shown} samples={samples} />
    </div>
  );
  const body = (
    <div className="push-notification__text">
      <Marked text={surface.body.shown} samples={samples} />
    </div>
  );
  // A stand-in for the app icon, not the real artwork.
  const icon = (
    <span className="push-notification__icon" aria-hidden="true">
      <i className="fas fa-music" />
    </span>
  );
  return (
    <div className="mb-3">
      <div className="small font-weight-bold mb-1">{surface.label}</div>
      <div className="push-device-stage">
        {surface.surface.startsWith('IOS') ? (
          <div className="push-notification push-notification--ios">
            {icon}
            <div className="push-notification__content">
              <div className="d-flex">
                <div className="flex-grow-1">{title}</div>
                <span className="push-notification__meta ml-2">now</span>
              </div>
              {body}
            </div>
          </div>
        ) : (
          <div className="push-notification push-notification--android">
            <div className="push-notification__meta d-flex align-items-center mb-1">
              {icon}
              SongPop · now
            </div>
            {title}
            {body}
          </div>
        )}
      </div>
      <div className="small mt-1">
        {cutLine('Title', surface.title)}
        {cutLine('Body', surface.body)}
        <div className="text-muted font-italic">{surface.note}</div>
      </div>
    </div>
  );
}

function LanguagePreview({
  broadcastId,
  language,
  titleTexts,
  bodyTexts,
  languages,
}: {
  broadcastId: string;
  language: string;
  titleTexts: PushTexts;
  bodyTexts: PushTexts;
  languages: ReadonlyArray<PushBroadcastLanguageOption>;
}): JSX.Element {
  const { data, previousData, loading, error } = useQuery<
    GetPushBroadcastPreviewQuery,
    GetPushBroadcastPreviewQueryVariables
  >(GET_PUSH_BROADCAST_PREVIEW, {
    variables: { id: broadcastId, language, titleTexts, bodyTexts },
    fetchPolicy: 'network-only',
  });
  const preview = (data ?? previousData)?.pushBroadcastPreview;
  const samples = (preview?.sampleValues ?? []).filter(({ isSample }) => isSample).map(({ value }) => value);

  return (
    <div className="border-top pt-2 mt-2">
      <div className="d-flex align-items-center mb-2">
        <span className="font-weight-bold mr-2">{pushLanguageName(language, languages)}</span>
        {preview?.isFallback ? (
          <SolidBadge tone="amber" className="mr-2">
            no override: receives {pushLanguageName(preview.language, languages)}
          </SolidBadge>
        ) : null}
        {loading ? <Spinner animation="border" size="sm" /> : null}
      </div>
      {error ? <PushIssueList issues={pushBroadcastErrorIssues(error)} title="Preview failed" /> : null}
      {preview ? (
        <>
          {preview.sampleValues.map(({ placeholder, value }) => (
            <div key={placeholder} className="small mb-2">
              <code>{placeholder}</code> is shown as <mark className="px-0">{value}</mark>, a <b>sample</b>: each player
              sees their own name.
            </div>
          ))}
          {preview.surfaces.map((surface) => (
            <SurfaceMock key={surface.surface} surface={surface} samples={samples} />
          ))}
          <details className="small mb-2">
            <summary>Full text as sent (before any lock-screen cut)</summary>
            <div className="font-weight-bold mt-1" style={{ whiteSpace: 'pre-wrap' }}>
              <Marked text={preview.title} samples={samples} />
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>
              <Marked text={preview.body} samples={samples} />
            </div>
          </details>
          <PushIssueList issues={preview.errors} title="The send would refuse these texts" />
          <PushIssueList issues={preview.warnings} title="Likely cut (not blocking)" variant="warning" />
        </>
      ) : null}
    </div>
  );
}

/**
 * The only place an operator sees what a player will see (there is no test send to an admin's phone): the push
 * rendered by the send's own code, in the one language picked, with each lock screen's cut points.
 */
export function PushPreview({
  broadcastId,
  titleTexts,
  bodyTexts,
  languages,
  sourceLanguage,
  overrideLanguages,
}: Props): JSX.Element {
  const [language, setLanguage] = useState(sourceLanguage);
  const texts = useSettled({ titleTexts, bodyTexts });
  // The signal the old chip row gave in bold, kept: which languages have their own text, which receive English.
  const codes = languages.map(({ code }) => code);
  const isTranslated = (code: string) => code === sourceLanguage || overrideLanguages.includes(code);
  const translated = codes.filter(isTranslated);
  const fallback = codes.filter((code) => !isTranslated(code));
  const option = (code: string, status: string) => (
    <option key={code} value={code}>
      {pushLanguageName(code, languages)} ({code}) · {status}
    </option>
  );

  return (
    <Card className="push-preview mb-3 border-primary">
      <Card.Header className="font-weight-bold bg-primary text-white">What the player sees</Card.Header>
      <Card.Body>
        {!broadcastId ? (
          <div className="text-muted">Create the draft once to see the rendered push.</div>
        ) : (
          <>
            <Form.Group controlId="push-preview-language" className="mb-1">
              <Form.Label className="small mb-1">Language to preview</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
              >
                <optgroup label={`Translated (${translated.length})`}>
                  {translated.map((code) =>
                    option(code, code === sourceLanguage ? 'mandatory text' : 'has an override')
                  )}
                </optgroup>
                {fallback.length > 0 ? (
                  <optgroup label={`No override, receives English (${fallback.length})`}>
                    {fallback.map((code) => option(code, 'receives English'))}
                  </optgroup>
                ) : null}
              </Form.Control>
            </Form.Group>
            {/* Which languages are translated, at a glance without opening the list (the old chip row's bold). */}
            <div className="small mb-1">
              Translated:{' '}
              <span className="font-weight-bold">
                {translated.map((code) => pushLanguageName(code, languages)).join(', ')}
              </span>
              {fallback.length > 0 ? (
                <span className="text-muted">
                  {' '}
                  · {fallback.length} other {fallback.length === 1 ? 'language receives' : 'languages receive'} English
                </span>
              ) : null}
            </div>
            <LanguagePreview
              key={language}
              broadcastId={broadcastId}
              language={language}
              titleTexts={texts.titleTexts}
              bodyTexts={texts.bodyTexts}
              languages={languages}
            />
            <div className="small text-muted mt-2">
              Cut points are estimates for a typical phone: screen width, font size and OS version move them.
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
